import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { requestPasswordReset, confirmPasswordReset } from '../lib/auth';
import Footer from '../components/ui/Footer';
import LanguageSelector from '@/components/ui/LanguageSelector';

type ForgotPasswordStep = 'email' | 'reset';

export default function ForgotPassword() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Form state
  const [step, setStep] = useState<ForgotPasswordStep>('email');
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [codeDestination, setCodeDestination] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = useState(false);

  // Handle email submission - request password reset code
  async function handleRequestCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const result = await requestPasswordReset(email, i18n.language);
      setCodeDestination(result.destination);
      setStep('reset');
    } catch (err) {
      const message = err instanceof Error ? err.message : t('forgotPassword.errors.generic');
      if (message.includes('UserNotFoundException') || message.includes('not found')) {
        setError(t('forgotPassword.errors.userNotFound'));
      } else if (message.includes('LimitExceededException') || message.includes('limit')) {
        setError(t('forgotPassword.errors.limitExceeded'));
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  }

  // Handle password reset with verification code
  async function handleResetPassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError(t('forgotPassword.errors.passwordMismatch'));
      return;
    }

    // Validate password strength
    if (newPassword.length < 8) {
      setError(t('forgotPassword.errors.passwordTooShort'));
      return;
    }

    setLoading(true);
    try {
      await confirmPasswordReset(email, verificationCode, newPassword, i18n.language);
      // Redirect to login with success state
      navigate('/login', { state: { passwordReset: true } });
    } catch (err) {
      const message = err instanceof Error ? err.message : t('forgotPassword.errors.generic');
      if (message.includes('CodeMismatchException') || message.includes('Invalid')) {
        setError(t('forgotPassword.errors.invalidCode'));
      } else if (message.includes('ExpiredCodeException') || message.includes('expired')) {
        setError(t('forgotPassword.errors.codeExpired'));
      } else if (message.includes('InvalidPasswordException') || message.includes('Password')) {
        setError(t('forgotPassword.errors.passwordTooWeak'));
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  }

  // Resend verification code
  async function handleResendCode() {
    setError(null);
    setResendSuccess(false);
    setLoading(true);
    try {
      await requestPasswordReset(email, i18n.language);
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('forgotPassword.errors.resendFailed'));
    } finally {
      setLoading(false);
    }
  }

  // Go back to email form
  function handleBackToEmail() {
    setStep('email');
    setVerificationCode('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header bar */}
      <header className="bg-gray-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-xl font-bold">
              <span className="text-white">MyPink</span>
              <span className="text-mk-pink-400">Contact</span>
            </Link>
            <LanguageSelector variant="light" />
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900">
              {step === 'reset' ? t('forgotPassword.resetTitle') : t('forgotPassword.title')}
            </h1>
            <p className="text-gray-600 mt-2">
              {step === 'reset'
                ? t('forgotPassword.resetSubtitle', { email: codeDestination })
                : t('forgotPassword.subtitle')}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            {step === 'email' ? (
              <>
                <form onSubmit={handleRequestCode} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('forgotPassword.email')}
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mk-pink-500 focus:border-transparent"
                      placeholder={t('forgotPassword.emailPlaceholder')}
                      required
                      autoFocus
                    />
                  </div>

                  {error && <p className="text-red-500 text-sm">{error}</p>}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 bg-mk-pink-600 text-white rounded-lg font-semibold hover:bg-mk-pink-700 transition-colors disabled:opacity-50"
                  >
                    {loading ? t('forgotPassword.sendingCode') : t('forgotPassword.sendCode')}
                  </button>
                </form>

                <p className="mt-6 text-center text-sm text-gray-600">
                  {t('forgotPassword.rememberPassword')}{' '}
                  <Link
                    to="/login"
                    className="text-mk-pink-600 hover:text-mk-pink-700 font-medium"
                  >
                    {t('forgotPassword.backToLogin')}
                  </Link>
                </p>
              </>
            ) : (
              <>
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('forgotPassword.verificationCode')}
                    </label>
                    <input
                      type="text"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mk-pink-500 focus:border-transparent text-center text-2xl tracking-widest font-mono"
                      placeholder="000000"
                      maxLength={6}
                      required
                      autoFocus
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('forgotPassword.newPassword')}
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mk-pink-500 focus:border-transparent"
                      placeholder={t('forgotPassword.newPasswordPlaceholder')}
                      required
                      minLength={8}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('forgotPassword.confirmPassword')}
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mk-pink-500 focus:border-transparent"
                      placeholder={t('forgotPassword.confirmPasswordPlaceholder')}
                      required
                    />
                  </div>

                  {error && <p className="text-red-500 text-sm">{error}</p>}
                  {resendSuccess && (
                    <p className="text-green-600 text-sm">{t('forgotPassword.codeSent')}</p>
                  )}

                  <button
                    type="submit"
                    disabled={loading || verificationCode.length < 6}
                    className="w-full py-3 bg-mk-pink-600 text-white rounded-lg font-semibold hover:bg-mk-pink-700 transition-colors disabled:opacity-50"
                  >
                    {loading ? t('forgotPassword.resetting') : t('forgotPassword.resetPassword')}
                  </button>
                </form>

                <div className="mt-4 text-center space-y-2">
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={loading}
                    className="text-mk-pink-600 hover:text-mk-pink-700 text-sm font-medium disabled:opacity-50"
                  >
                    {t('forgotPassword.resendCode')}
                  </button>

                  <div>
                    <button
                      type="button"
                      onClick={handleBackToEmail}
                      className="text-gray-500 hover:text-gray-700 text-sm"
                    >
                      {t('forgotPassword.changeEmail')}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <Footer showCta={false} />
    </div>
  );
}
